/**
 * OfflineBanner - ambient connectivity indicator above main content.
 *
 * Shows when offline (amber) or while replaying the offline queue (blue).
 * Hidden when online with an empty queue.
 */
import { Loader2, WifiOff } from "lucide-react";

import { useOfflineQueue } from "@/shared/lib/useOfflineQueue";

/** Connectivity banner - mounted in AppLayout above page content. */
export function OfflineBanner() {
  const { isOnline, isSyncing, queueLength } = useOfflineQueue();

  if (isSyncing) {
    return (
      <div
        role="status"
        className="mb-4 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-4 py-2.5 text-sm text-primary"
      >
        <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
        <span>
          Syncing {queueLength} item{queueLength === 1 ? "" : "s"}...
        </span>
      </div>
    );
  }

  if (!isOnline) {
    const itemLabel = queueLength === 1 ? "item" : "items";
    return (
      <div
        role="status"
        className="mb-4 flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-2.5 text-sm text-warning"
      >
        <WifiOff className="size-4 shrink-0" aria-hidden />
        <span>
          Offline - {queueLength} {itemLabel} queued.
          {queueLength > 0
            ? " Your work is saved and will sync when you're back online."
            : ""}
        </span>
      </div>
    );
  }

  return null;
}
